/**
 * Hook for order management — fetching, filtering, and status transitions.
 */

import { useState, useCallback, useMemo, useRef } from 'react'
import { apiClient } from '@/services/apiClient'
import type {
  Order, OrderStats, OrderQueueStats, OrderCreatePayload, OrderUpdatePayload, QuoteOutcome,
  OrderScenario, OrderRevision, FeasibilityRecord, OrderFeasibilityState, FeasibilityPayload,
  QueueState, WaitingOn, OrderEventItem, OrderMeasures,
  GateState, OrderGateRow, OrderGateCommitment, OrderGatesPayload,
  IntakeAgenda, IntakeExtraction, IntakeRead, IntakeUpdatePayload, QuoteRecordCreatePayload,
  OrderProcessSummary, OrderQuoteSummary,
  CustomerUpdate, CustomerUpdateApplyPayload, CustomerUpdateProposal, CustomerUpdateStatus, CustomerUpdateSummary,
  IntakeField, ProposedChange,
  FirstQuote, OrderDuplicate, OrderOwner, OwnerBasis,
} from '@/types/commerce'
import { customerUpdateErrorMessage } from '@/components/records/customerUpdateText'
import { useNavBadgeStore } from '@/stores/navBadgeStore'
import { useFeatureStore } from '@/stores/featureStore'
import { queryClient } from '@/services/queryClient'
import { processKey } from '@/hooks/useProcess'
import { DEFAULT_SORT, isSortKey, readSort, type ModuleSorts, type RegisterSort, type SortDir } from '@/components/records/registerSort'
import { LINK_FAILED } from '@/components/orders/intakeLinkText'

/**
 * SA-1026: a feasibility write moves the record's process (the wait on
 * `record.feasibility` ends, the next step opens), but this lane is
 * hand-rolled and the process reads live under React Query's `['process']`
 * keys at the app's five-minute staleTime. Invalidate them at the write
 * site, so a band, a Tasks section or a task page mounted after the write
 * never serves the old step. The module client, not a hook: `OrdersPage`
 * and this hook call no React Query hook by rule (SA-397).
 */
function invalidateProcessReads(): void {
  void queryClient.invalidateQueries({ queryKey: processKey })
}

// ── snake_case → camelCase transformers ──────────────────────────────

function transformPayment(p: Record<string, unknown>): Record<string, unknown> {
  return {
    id: p.id,
    orderId: p.order_id,
    amountCents: p.amount_cents,
    currency: p.currency,
    method: p.method,
    paymentType: p.payment_type,
    stripePaymentLinkUrl: p.stripe_payment_link_url,
    status: p.status,
    createdAt: p.created_at,
    completedAt: p.completed_at,
  }
}

type StepSummary = NonNullable<OrderProcessSummary['currentStep']>

// E4 register (SA-397): the list's per-row extras. Absent or null on every
// response that did not ask for them.
function transformProcessSummary(p: Record<string, unknown> | null | undefined): OrderProcessSummary | null {
  if (!p) return null
  const step = p.current_step as Record<string, unknown> | null | undefined
  return {
    instanceId: p.instance_id as string,
    currentStep: step ? {
      id: step.id as string,
      number: step.number as number,
      title: step.title as string,
      seat: (step.seat as string) ?? null,
      seatLabel: (step.seat_label as string) ?? null,
      executorType: (step.executor_type as StepSummary['executorType']) ?? null,
      level: (step.level as string) ?? null,
    } : null,
    openTaskCount: (p.open_task_count as number) || 0,
    openTaskId: (p.open_task_id as string | null | undefined) ?? null,
    openTaskCanOpen: (p.open_task_can_open as boolean | null | undefined) ?? null,
    dueAt: (p.due_at as string) ?? null,
    overdue: Boolean(p.overdue),
  }
}

function transformCustomerUpdateSummary(s: Record<string, unknown> | null | undefined): CustomerUpdateSummary | null {
  if (!s) return null
  return { waiting: s.waiting as number, responses: (s.responses as number | undefined) ?? 0, latestReceivedAt: s.latest_received_at as string }
}

const OWNER_BASES: readonly OwnerBasis[] = ['approved', 'entered', 'started']

function transformOwner(o: Record<string, unknown> | null | undefined): OrderOwner | null {
  if (!o || !OWNER_BASES.includes(o.basis as OwnerBasis)) return null
  return { name: (o.name as string | null) ?? null, basis: o.basis as OwnerBasis }
}

function transformDuplicate(d: Record<string, unknown> | null | undefined): OrderDuplicate | null {
  if (!d || typeof d.record_number !== 'string' || typeof d.score !== 'number') return null
  return { recordNumber: d.record_number, score: d.score }
}

/** SA-1204: null unless the clock is served whole (a string receive time, a numeric target). */
function transformFirstQuote(f: Record<string, unknown> | null | undefined): FirstQuote | null {
  if (!f || typeof f.received_at !== 'string' || typeof f.target_minutes !== 'number') return null
  return {
    receivedAt: f.received_at,
    sentAt: typeof f.sent_at === 'string' ? f.sent_at : null,
    minutes: typeof f.minutes === 'number' ? f.minutes : null,
    targetMinutes: f.target_minutes,
  }
}

/** SA-1042: the server's diff as served; the field keeps the intake read's snake_case shape. */
export function transformProposedChanges(raw: unknown): ProposedChange[] | null {
  if (!Array.isArray(raw)) return null
  return raw.map((c) => {
    const change = c as Record<string, unknown>
    return { field: change.field as IntakeField, proposed: change.proposed }
  })
}

export function transformCustomerUpdate(u: Record<string, unknown>): CustomerUpdate {
  const proposal = u.proposal as Record<string, unknown> | null
  return {
    id: u.id as string,
    kind: u.kind === 'response' ? 'response' : 'update',
    status: u.status as CustomerUpdateStatus,
    channel: u.channel as string,
    intent: u.intent as string,
    confidence: (u.confidence as number | null) ?? null,
    receivedAt: u.received_at as string,
    createdAt: u.created_at as string,
    conversationId: (u.conversation_id as string | null) ?? null,
    messageId: (u.message_id as string | null) ?? null,
    fromName: (u.from_name as string | null) ?? null,
    excerpt: (u.excerpt as string | null) ?? null,
    decidedByName: (u.decided_by_name as string | null) ?? null,
    proposal: proposal ? {
      values: (proposal.values as Record<string, unknown>) ?? {},
      provenance: (proposal.provenance as Record<string, 'extracted'>) ?? {},
      dropped: (proposal.dropped as CustomerUpdateProposal['dropped']) ?? [],
      model: (proposal.model as string) ?? '',
      extractedAt: (proposal.extracted_at as string | null) ?? null,
    } : null,
    changes: transformProposedChanges(u.changes),
    settledByName: (u.settled_by_name as string | null) ?? null,
    settledAt: (u.settled_at as string | null) ?? null,
    revisionNumber: (u.revision_number as number | null) ?? null,
    fieldsApplied: (u.fields_applied as string[] | null) ?? null,
    canApply: Boolean(u.can_apply),
    applyRefusal: (u.apply_refusal as string | null) ?? null,
  }
}

function transformQuoteSummary(q: Record<string, unknown> | null | undefined): OrderQuoteSummary | null {
  if (!q) return null
  return {
    versionNumber: q.version_number as number,
    amountCents: q.amount_cents as number,
    currency: q.currency as string,
    validUntil: q.valid_until as string,
    status: q.status as string,
    expired: Boolean(q.expired),
    counterAmountCents: (q.counter_amount_cents as number | null | undefined) ?? null,
  }
}

export function transformOrder(o: Record<string, unknown>): Order {
  return {
    id: o.id as string,
    userId: o.user_id as string,
    contactId: o.contact_id as string | null,
    conversationId: o.conversation_id as string | null,
    personaId: o.persona_id as string | null,
    orderNumber: o.order_number as string,
    orderType: o.order_type as string,
    channel: o.channel as string,
    status: o.status as Order['status'],
    items: o.items as Order['items'],
    eventDate: o.event_date as string | null,
    eventTime: o.event_time as string | null,
    occasion: o.occasion as string | null,
    guestCount: o.guest_count as number | null,
    specialInstructions: o.special_instructions as string | null,
    customDetails: o.custom_details as Record<string, unknown> | null,
    deliveryMethod: o.delivery_method as string | null,
    deliveryAddress: o.delivery_address as string | null,
    deliveryZone: o.delivery_zone as string | null,
    pickupLocation: o.pickup_location as string | null,
    deliveryDate: o.delivery_date as string | null,
    deliveryTimeWindow: o.delivery_time_window as string | null,
    deliveryFeeCents: (o.delivery_fee_cents as number) || 0,
    currency: o.currency as string,
    subtotalCents: (o.subtotal_cents as number) || 0,
    discountCents: (o.discount_cents as number) || 0,
    taxCents: (o.tax_cents as number) || 0,
    totalCents: (o.total_cents as number) || 0,
    depositCents: (o.deposit_cents as number) || 0,
    depositPaid: (o.deposit_paid as boolean) || false,
    balancePaid: (o.balance_paid as boolean) || false,
    customerName: o.customer_name as string | null,
    customerPhone: o.customer_phone as string | null,
    customerEmail: o.customer_email as string | null,
    quotedAmountCents: o.quoted_amount_cents as number | null,
    quotedBy: o.quoted_by as string | null,
    quoteNotes: o.quote_notes as string | null,
    outcome: (o.outcome as QuoteOutcome) ?? null,
    outcomeNote: (o.outcome_note as string) ?? null,
    closedAt: (o.closed_at as string) ?? null,
    decisionRequestedAt: (o.decision_requested_at as string) ?? null,
    queueState: (o.queue_state as QueueState) ?? null,
    queueStateEnteredAt: (o.queue_state_entered_at as string) ?? null,
    waitingOn: (o.waiting_on as WaitingOn) ?? null,
    assignedTo: (o.assigned_to as string) ?? null,
    priority: (o.priority as Order['priority']) ?? null,
    reuseOfOrderId: (o.reuse_of_order_id as string) ?? null,
    reuseOfOrderNumber: (o.reuse_of_order_number as string) ?? null,
    process: transformProcessSummary(o.process as Record<string, unknown> | null),
    quote: transformQuoteSummary(o.quote as Record<string, unknown> | null),
    commercialStatus: (o.commercial_status as Order['commercialStatus']) ?? null,
    commercialStatusOverride: (o.commercial_status_override as string) ?? null,
    commercialStatusOverrideNote: (o.commercial_status_override_note as string) ?? null,
    customerUpdate: transformCustomerUpdateSummary(o.customer_update as Record<string, unknown> | null),
    owner: transformOwner(o.owner as Record<string, unknown> | null),
    duplicate: transformDuplicate(o.duplicate as Record<string, unknown> | null),
    firstQuote: transformFirstQuote(o.first_quote as Record<string, unknown> | null),
    notes: o.notes as string | null,
    payments: Array.isArray(o.payments)
      ? (o.payments as Record<string, unknown>[]).map(transformPayment) as unknown as Order['payments']
      : null,
    createdAt: o.created_at as string | null,
    updatedAt: o.updated_at as string | null,
    quotedAt: o.quoted_at as string | null,
    paidAt: o.paid_at as string | null,
    deliveredAt: o.delivered_at as string | null,
    cancelledAt: o.cancelled_at as string | null,
    seenAt: (o.seen_at as string) ?? null,
  }
}

function transformQueueStats(q?: Record<string, unknown>): OrderQueueStats {
  return {
    total: ((q?.total as number) ?? 0) || 0,
    byState: (q?.by_state as Record<string, number>) ?? {},
    byWaitingOn: (q?.by_waiting_on as Record<string, number>) ?? {},
  }
}

function transformStats(s: Record<string, unknown>): OrderStats {
  return {
    total: (s.total as number) || 0,
    new: (s.new as number) || 0,
    draft: (s.draft as number) || 0,
    pendingQuote: (s.pending_quote as number) || 0,
    quoted: (s.quoted as number) || 0,
    pendingPayment: (s.pending_payment as number) || 0,
    depositPaid: (s.deposit_paid as number) || 0,
    fullyPaid: (s.fully_paid as number) || 0,
    inProduction: (s.in_production as number) || 0,
    ready: (s.ready as number) || 0,
    delivered: (s.delivered as number) || 0,
    cancelled: (s.cancelled as number) || 0,
    totalRevenueCents: (s.total_revenue_cents as number) || 0,
    currency: (s.currency as string) || '',
    queue: transformQueueStats(s.queue as Record<string, unknown> | undefined),
  }
}

function transformScenario(s: Record<string, unknown>): OrderScenario {
  return {
    id: s.id as string,
    orderId: s.order_id as string,
    scenarioNumber: s.scenario_number as number,
    label: s.label as string,
    details: (s.details as Record<string, unknown>) ?? null,
    status: s.status as OrderScenario['status'],
    limitingFactor: (s.limiting_factor as string) ?? null,
    createdAt: (s.created_at as string) ?? null,
    decidedAt: (s.decided_at as string) ?? null,
  }
}

function transformRevision(r: Record<string, unknown>): OrderRevision {
  return {
    id: r.id as string,
    orderId: r.order_id as string,
    revisionNumber: r.revision_number as number,
    changedFields: (r.changed_fields as OrderRevision['changedFields']) ?? null,
    reason: (r.reason as string) ?? null,
    sourceConversationId: (r.source_conversation_id as string) ?? null,
    createdAt: (r.created_at as string) ?? null,
  }
}

function transformFeasibility(f: Record<string, unknown> | null | undefined): FeasibilityRecord | null {
  if (!f) return null
  return {
    decisionState: f.decision_state as FeasibilityRecord['decisionState'],
    limitingFactor: (f.limiting_factor as string) ?? null,
    requestedVsSupportable: (f.requested_vs_supportable as Record<string, unknown>) ?? null,
    note: (f.note as string) ?? null,
    recordedBy: (f.recorded_by as string) ?? null,
    recordedAt: (f.recorded_at as string) ?? null,
    optionCount: (f.option_count as number) ?? 0,
  }
}

function mapFeasibilityState(raw: Record<string, unknown>): OrderFeasibilityState {
  return {
    order: transformOrder(raw.order as Record<string, unknown>),
    feasibility: transformFeasibility(raw.feasibility as Record<string, unknown> | null),
    scenarios: Array.isArray(raw.scenarios)
      ? (raw.scenarios as Record<string, unknown>[]).map(transformScenario)
      : [],
    revisions: Array.isArray(raw.revisions)
      ? (raw.revisions as Record<string, unknown>[]).map(transformRevision)
      : [],
  }
}

function transformOrderEvent(e: Record<string, unknown>): OrderEventItem {
  return {
    id: e.id as string,
    eventType: e.event_type as string,
    payload: (e.payload as Record<string, unknown>) ?? null,
    actorType: e.actor_type as string,
    actorId: (e.actor_id as string) ?? null,
    occurredAt: e.occurred_at as string,
  }
}

function transformMeasures(m: Record<string, unknown>): OrderMeasures {
  const waitByOwner = (m.wait_by_owner_minutes as Record<string, number>) || {}
  const revisionResponse = (m.revision_response_minutes as Record<string, unknown>) || {}
  const decisionLatency = (m.decision_latency_minutes as Record<string, unknown>) || {}
  return {
    endToEndMinutes: (m.end_to_end_minutes as number) ?? null,
    touchTimeMinutes: (m.touch_time_minutes as number) ?? 0,
    touchTimeMethod: (m.touch_time_method as string) ?? '',
    waitByOwnerMinutes: {
      customer: (waitByOwner.customer as number) ?? 0,
      commercial: (waitByOwner.commercial as number) ?? 0,
      specialist: (waitByOwner.specialist as number) ?? 0,
      dispatch: (waitByOwner.dispatch as number) ?? 0,
    },
    revisionResponseMinutes: {
      samples: (revisionResponse.samples as number[]) ?? [],
      median: (revisionResponse.median as number) ?? null,
    },
    decisionLatencyMinutes: {
      samples: (decisionLatency.samples as number[]) ?? [],
      openSince: (decisionLatency.open_since as string) ?? null,
    },
    requestToFirstQuoteMinutes: (m.request_to_first_quote_minutes as number) ?? null,
  }
}

function transformGateRow(g: Record<string, unknown>): OrderGateRow {
  return {
    department: g.department as string,
    state: g.state as GateState,
    enteredAt: g.entered_at as string,
    enteredBy: (g.entered_by as string) ?? null,
    ruleId: (g.rule_id as string) ?? null,
    note: (g.note as string) ?? null,
  }
}

function transformGateCommitment(c: Record<string, unknown> | null | undefined): OrderGateCommitment | null {
  if (!c) return null
  return {
    worstGate: (c.worst_gate as GateState) ?? null,
    unresolved: Array.isArray(c.unresolved) ? (c.unresolved as string[]) : [],
    gates: (c.gates as Record<string, GateState>) ?? {},
    decisionState: (c.decision_state as string) ?? null,
    qualified: Boolean(c.qualified),
  }
}

function transformGatesPayload(raw: Record<string, unknown>): OrderGatesPayload {
  return {
    gates: Array.isArray(raw.gates) ? (raw.gates as Record<string, unknown>[]).map(transformGateRow) : [],
    commitment: transformGateCommitment(raw.commitment as Record<string, unknown> | null | undefined),
  }
}

// ── Hook ─────────────────────────────────────────────────────────────

interface UseOrdersReturn {
  orders: Order[]
  total: number
  // SA-285: True only on the priority-sorted path when the scored set hit
  // the server's cap — the ranking is then over the newest cap-many
  // missions, not the whole queue. Always false on other list paths.
  capped: boolean
  currentOrder: Order | null
  stats: OrderStats | null
  isLoading: boolean
  error: string | null

  // Filters
  statusFilter: string
  orderTypeFilter: string
  searchQuery: string
  contactId: string | null
  queueStateFilter: string
  /** SA-1175: who holds the quote (`waiting_on`), a Home legend deep link. */
  waitingOnFilter: string
  /** E4 register (SA-397): the current step's id / a commercial status key. */
  stepFilter: string
  commercialStatusFilter: string
  /** Home deep link (spec D11/D14): a comma list over the current quote
   *  version's status; `process_state=not_started` (D12). */
  quoteStatusFilter: string
  processStateFilter: string
  /** SA-717: also list the records closed as not an enquiry. */
  includeRejectedFilter: boolean
  sortBy: string
  sortDir: SortDir | null
  page: number

  // Actions
  fetchOrders: (opts?: { silent?: boolean }) => Promise<void>
  fetchOrderDetail: (orderId: string) => Promise<void>
  fetchStats: () => Promise<void>
  createOrder: (payload: OrderCreatePayload) => Promise<Order>
  updateOrder: (orderId: string, payload: OrderUpdatePayload) => Promise<void>
  updateStatus: (orderId: string, status: string) => Promise<void>
  enterQuote: (orderId: string, amountCents: number, notes?: string) => Promise<void>
  updateNotes: (orderId: string, notes: string) => Promise<void>
  recordPayment: (orderId: string, p: { amountCents: number; method: string }) => Promise<void>
  closeOutcome: (orderId: string, p: { outcome: QuoteOutcome; note?: string }) => Promise<void>
  /** SA-785: undo a Not an enquiry from the record page. */
  reinstateRecord: (orderId: string) => Promise<void>
  getFeasibility: (orderId: string) => Promise<OrderFeasibilityState>
  recordFeasibility: (orderId: string, p: FeasibilityPayload) => Promise<OrderFeasibilityState>
  selectScenario: (orderId: string, scenarioId: string) => Promise<OrderFeasibilityState>
  // SA-270 queue lane
  updateQueueState: (
    orderId: string, p: { queueState: QueueState; waitingOn?: WaitingOn; reason: string },
  ) => Promise<Order>
  assignOrder: (orderId: string, userId: string | null) => Promise<Order>
  getOrderEvents: (orderId: string, limit?: number) => Promise<OrderEventItem[]>
  getOrderMeasures: (orderId: string) => Promise<OrderMeasures>
  getAssignees: () => Promise<Array<{ id: string; name: string }>>
  // SA-284 department gates
  getOrderGates: (orderId: string) => Promise<OrderGatesPayload>
  updateGateState: (
    orderId: string, department: string, p: { state: GateState; reason?: string },
  ) => Promise<OrderGatesPayload>
  // SA-271 duplicate resolution
  resolveDuplicate: (
    orderId: string, p: { action: 'proceed_new' | 'reuse_delta'; matchedOrderId?: string; reason: string },
  ) => Promise<Order>
  // SA-401 record-first intake
  getIntakeAgendas: () => Promise<IntakeAgenda[]>
  extractIntake: (agenda: string, text: string, personaId?: string) => Promise<IntakeExtraction>
  createQuoteRecord: (payload: QuoteRecordCreatePayload) => Promise<Order>
  getIntake: (orderId: string) => Promise<IntakeRead>
  updateIntake: (orderId: string, payload: IntakeUpdatePayload) => Promise<IntakeRead>
  linkIntakeCompany: (orderId: string, slot: string, companyId: string | null) => Promise<IntakeRead>
  getCustomerUpdates: (orderId: string) => Promise<CustomerUpdate[]>
  extractCustomerUpdate: (orderId: string, updateId: string) => Promise<CustomerUpdate>
  applyCustomerUpdate: (orderId: string, updateId: string, payload: CustomerUpdateApplyPayload) => Promise<CustomerUpdate>
  dismissCustomerUpdate: (orderId: string, updateId: string, reason?: string) => Promise<CustomerUpdate>
  setStatusFilter: (v: string) => void
  setOrderTypeFilter: (v: string) => void
  setSearchQuery: (v: string) => void
  setContactId: (v: string | null) => void
  setQueueStateFilter: (v: string) => void
  setWaitingOnFilter: (v: string) => void
  setStepFilter: (v: string) => void
  setCommercialStatusFilter: (v: string) => void
  setQuoteStatusFilter: (v: string) => void
  setProcessStateFilter: (v: string) => void
  setIncludeRejectedFilter: (v: boolean) => void
  setSortBy: (v: string) => void
  /** SA-1124: a register column's sort, or null for the default; returns to page 1. */
  setSort: (next: RegisterSort | null) => void
  setPage: (v: number) => void
  setCurrentOrder: (o: Order | null) => void
}

export interface UseOrdersInitial {
  contactId?: string | null
  searchQuery?: string | null
  /** Home deep links (spec D14): seeded once, on mount, the way `contactId`
   *  and `searchQuery` already are. `step` is deliberately NOT here —
   *  `?step=<id>` is owned by the SA-460 record-lane step drawer (fix round
   *  1, Task 15 finding 1), and no Home widget ever emits it. */
  commercialStatus?: string | null
  quoteStatus?: string | null
  processState?: string | null
  /** SA-1175: `?waiting_on=`, one of the four roles (the page checks it first). */
  waitingOn?: string | null
  /** SA-1124: the URL's `?sort=&dir=`, read once at mount (`readSort` drops
   *  a key the server would refuse). */
  sort?: string | null
  sortDir?: string | null
  /** SA-1183: the module views' own sort keys (`CustomSlots.moduleSorts`), read once at mount. */
  moduleSorts?: ModuleSorts
}

/** The server's default `per_page` (`orders.list_orders`; pinned by
 *  `test_the_default_page_is_fifty`). The list request deliberately sends no
 *  `per_page` (SA-411 D9), so this mirror is what the pager counts in. */
export const ORDERS_PAGE_SIZE = 50

export function useOrders(initial?: UseOrdersInitial): UseOrdersReturn {
  const [orders, setOrders] = useState<Order[]>([])
  const [total, setTotal] = useState(0)
  const [capped, setCapped] = useState(false)
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null)
  const [stats, setStats] = useState<OrderStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [statusFilter, setStatusFilterRaw] = useState('')
  const [orderTypeFilter, setOrderTypeFilterRaw] = useState('')
  const [searchQuery, setSearchQueryRaw] = useState(initial?.searchQuery ?? '')
  const [contactId, setContactIdRaw] = useState<string | null>(initial?.contactId ?? null)
  const [queueStateFilter, setQueueStateFilterRaw] = useState('')
  const [waitingOnFilter, setWaitingOnFilterRaw] = useState(initial?.waitingOn ?? '')
  const [stepFilter, setStepFilterRaw] = useState('')
  const [commercialStatusFilter, setCommercialStatusFilterRaw] = useState(initial?.commercialStatus ?? '')
  const [quoteStatusFilter, setQuoteStatusFilterRaw] = useState(initial?.quoteStatus ?? '')
  const [processStateFilter, setProcessStateFilterRaw] = useState(initial?.processState ?? '')
  const [includeRejectedFilter, setIncludeRejectedFilterRaw] = useState(false)
  // Strict (loaded AND on), not useFeature's assume-enabled default: an
  // unloaded manifest must not change the request. It is a fetchOrders
  // dependency, so the list refetches with the includes once it loads.
  const processEngine = useFeatureStore((s) => s.isLoaded && s.features.has('process_engine'))
  const [moduleSorts] = useState<ModuleSorts>(() => initial?.moduleSorts ?? {})
  const [sortBy, setSortByRaw] = useState(() => readSort(initial?.sort ?? null, initial?.sortDir ?? null, moduleSorts).sortBy)
  const [sortDir, setSortDirRaw] = useState<SortDir | null>(
    () => readSort(initial?.sort ?? null, initial?.sortDir ?? null, moduleSorts).sortDir)
  const [page, setPage] = useState(1)

  // SA-411 D10: a change of WHAT is listed returns to page 1 in the same
  // batch — one render, one fetch. An effect would fetch page N of the new
  // filter first. Stable identities: the page's effects depend on them.
  const toFirstPage = useCallback(
    <T>(set: (v: T) => void) => (v: T) => { set(v); setPage(1) }, [])
  const setStatusFilter = useMemo(() => toFirstPage(setStatusFilterRaw), [toFirstPage])
  const setOrderTypeFilter = useMemo(() => toFirstPage(setOrderTypeFilterRaw), [toFirstPage])
  const setSearchQuery = useMemo(() => toFirstPage(setSearchQueryRaw), [toFirstPage])
  const setContactId = useMemo(() => toFirstPage<string | null>(setContactIdRaw), [toFirstPage])
  const setQueueStateFilter = useMemo(() => toFirstPage(setQueueStateFilterRaw), [toFirstPage])
  const setStepFilter = useMemo(() => toFirstPage(setStepFilterRaw), [toFirstPage])
  const setCommercialStatusFilter = useMemo(
    () => toFirstPage(setCommercialStatusFilterRaw), [toFirstPage])
  const setQuoteStatusFilter = useMemo(() => toFirstPage(setQuoteStatusFilterRaw), [toFirstPage])
  const setProcessStateFilter = useMemo(() => toFirstPage(setProcessStateFilterRaw), [toFirstPage])
  const setWaitingOnFilter = useMemo(() => toFirstPage(setWaitingOnFilterRaw), [toFirstPage])
  const setIncludeRejectedFilter = useMemo(
    () => toFirstPage<boolean>(setIncludeRejectedFilterRaw), [toFirstPage])
  // The legacy select names keys that take no direction.
  const setSortBy = useMemo(
    () => toFirstPage((v: string) => { setSortByRaw(v); setSortDirRaw(null) }), [toFirstPage])
  // SA-1124: a register header's sort, or null for the default.
  const setSort = useMemo(() => toFirstPage((next: RegisterSort | null) => {
    setSortByRaw(next ? next.key : DEFAULT_SORT)
    setSortDirRaw(next ? next.dir : null)
  }), [toFirstPage])

  // Monotonic request id so a slower, superseded list fetch (e.g. an
  // unfiltered scan that resolves after a filtered one) can't clobber a
  // later response's state — see fix/view-orders-contact-filter.
  const requestSeq = useRef(0)

  const fetchOrders = useCallback(async (opts?: { silent?: boolean }) => {
    const id = ++requestSeq.current
    if (!opts?.silent) setIsLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      if (orderTypeFilter) params.set('order_type', orderTypeFilter)
      if (searchQuery) params.set('search', searchQuery)
      if (contactId) params.set('contact_id', contactId)
      if (queueStateFilter) params.set('queue_state', queueStateFilter)
      // SA-1175: the holder, a column on every deployment — sent whenever set.
      if (waitingOnFilter) params.set('waiting_on', waitingOnFilter)
      // Home deep link (spec D11): a quote-version filter, gated on
      // `workflow_engine` server-side — matches nothing without it, so the
      // request stays safe to send unconditionally.
      if (quoteStatusFilter) params.set('quote_status', quoteStatusFilter)
      // SA-717: the register leaves rejected records out unless asked. Sent
      // only when set, so every other request is byte-for-byte what it was.
      if (includeRejectedFilter) params.set('include_rejected', 'true')
      // E4 register (SA-397, spec §9.3): the per-row extras and their
      // filters exist only on a process_engine deployment; everywhere else
      // the request is byte-for-byte what it was. `updates` (SA-1029) is the
      // waiting customer-update summary the register's Update waiting chip reads;
      // `owner` (SA-1035) is the record owner its Owner column reads.
      if (processEngine) {
        params.set('include', 'process,quotes,status,updates,owner')
        if (stepFilter) params.set('step', stepFilter)
        if (commercialStatusFilter) params.set('commercial_status', commercialStatusFilter)
        // Home deep link (spec D12): matches nothing without process_engine.
        if (processStateFilter) params.set('process_state', processStateFilter)
      }
      params.set('sort', sortBy)
      // SA-1124: only a column key takes a direction; a legacy key's request is unchanged.
      if (sortDir && isSortKey(sortBy, moduleSorts)) params.set('dir', sortDir)
      params.set('page', String(page))

      const res = await apiClient.fetch(`/api/commerce/orders?${params}`)
      if (!res.ok) throw new Error('Failed to fetch orders')
      const data = await res.json()
      if (id !== requestSeq.current) return
      const items = data.items || []
      const listTotal = data.total || 0
      // The last row of the last page was closed or filtered away (a silent
      // refetch): step back to the last page that exists. `page` is a
      // dependency of this callback, so the step-back refetches — but only
      // when it MOVES: if the total still claims this page (a row vanished
      // between the count and the select), setPage would be a no-op, nothing
      // would refetch, and the previous page's rows would stay on screen.
      const lastPage = Math.max(1, Math.ceil(listTotal / ORDERS_PAGE_SIZE))
      if (items.length === 0 && page > 1 && lastPage !== page) {
        setPage(Math.min(lastPage, page - 1))
        return
      }
      setOrders(items.map(transformOrder))
      setTotal(listTotal)
      setCapped(Boolean(data.capped))
    } catch (e) {
      if (id !== requestSeq.current) return
      setError((e as Error).message)
    } finally {
      if (id === requestSeq.current) setIsLoading(false)
    }
  }, [statusFilter, orderTypeFilter, searchQuery, contactId, queueStateFilter, waitingOnFilter, stepFilter,
    commercialStatusFilter, quoteStatusFilter, processStateFilter, includeRejectedFilter, processEngine, sortBy, sortDir, moduleSorts, page])

  const fetchOrderDetail = useCallback(async (orderId: string) => {
    setIsLoading(true)
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}`)
      if (!res.ok) {
        let detail = 'Order not found'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const data = await res.json()
      setCurrentOrder(transformOrder(data))
      // Mark seen (clears 'new'); best-effort, then refresh the nav badge.
      try {
        await apiClient.fetch(`/api/commerce/orders/${orderId}/seen`, { method: 'PUT' })
        const s = await apiClient.fetch('/api/commerce/orders/stats')
        if (s.ok) {
          const sd = await s.json()
          useNavBadgeStore.getState().setOrdersNew((sd.new as number) ?? 0)
        }
      } catch { /* silent */ }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/commerce/orders/stats')
      if (!res.ok) return
      const data = await res.json()
      setStats(transformStats(data))
    } catch {
      // silent
    }
  }, [])

  const createOrder = useCallback(async (payload: OrderCreatePayload): Promise<Order> => {
    const res = await apiClient.fetch('/api/commerce/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      let detail = 'Failed to create order'
      try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
      throw new Error(detail)
    }
    const order = transformOrder(await res.json())
    await Promise.all([fetchOrders({ silent: true }), fetchStats()])
    return order
  }, [fetchOrders, fetchStats])

  const updateStatus = useCallback(async (orderId: string, status: string) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to update status')
      }
      const data = await res.json()
      const updated = transformOrder(data)
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const enterQuote = useCallback(async (orderId: string, amountCents: number, notes?: string) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/quote`, {
        method: 'PUT',
        body: JSON.stringify({ quoted_amount_cents: amountCents, quote_notes: notes || null }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to enter quote')
      }
      const data = await res.json()
      const updated = transformOrder(data)
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const recordPayment = useCallback(async (
    orderId: string, p: { amountCents: number; method: string },
  ) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: p.amountCents, method: p.method }),
      })
      if (!res.ok) {
        let detail = 'Failed to record payment'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const closeOutcome = useCallback(async (
    orderId: string, p: { outcome: QuoteOutcome; note?: string },
  ) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome: p.outcome, outcome_note: p.note ?? null }),
      })
      if (!res.ok) {
        let detail = 'Failed to close outcome'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const reinstateRecord = useCallback(async (orderId: string) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/reinstate`, { method: 'POST' })
      if (!res.ok) {
        let detail = 'Failed to reinstate'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const getFeasibility = useCallback(async (orderId: string): Promise<OrderFeasibilityState> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/feasibility`)
      if (!res.ok) throw new Error('Failed to load feasibility')
      return mapFeasibilityState(await res.json())
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const recordFeasibility = useCallback(async (
    orderId: string, p: FeasibilityPayload,
  ): Promise<OrderFeasibilityState> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/feasibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision_state: p.decisionState,
          limiting_factor: p.limitingFactor ?? null,
          requested_vs_supportable: p.requestedVsSupportable ?? null,
          note: p.note ?? null,
          options: p.options.map(o => ({ label: o.label, details: o.details ?? null })),
        }),
      })
      if (!res.ok) {
        let detail = 'Failed to record feasibility'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const state = mapFeasibilityState(await res.json())
      setCurrentOrder(state.order)
      setOrders(prev => prev.map(o => o.id === orderId ? state.order : o))
      invalidateProcessReads()
      return state
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const selectScenario = useCallback(async (
    orderId: string, scenarioId: string,
  ): Promise<OrderFeasibilityState> => {
    try {
      const res = await apiClient.fetch(
        `/api/commerce/orders/${orderId}/scenarios/${scenarioId}/select`,
        { method: 'POST' },
      )
      if (!res.ok) {
        let detail = 'Failed to select scenario'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const state = mapFeasibilityState(await res.json())
      setCurrentOrder(state.order)
      setOrders(prev => prev.map(o => o.id === orderId ? state.order : o))
      invalidateProcessReads()
      return state
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const updateQueueState = useCallback(async (
    orderId: string, p: { queueState: QueueState; waitingOn?: WaitingOn; reason: string },
  ): Promise<Order> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/queue-state`, {
        method: 'PUT',
        body: JSON.stringify({
          queue_state: p.queueState,
          waiting_on: p.waitingOn ?? null,
          reason: p.reason,
        }),
      })
      if (!res.ok) {
        let detail = 'Failed to update queue state'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
      return updated
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  // SA-271: resolve a potential_duplicate mission (proceed as new, or
  // link it as a reuse/delta of a matched prior mission).
  const resolveDuplicate = useCallback(async (
    orderId: string,
    p: { action: 'proceed_new' | 'reuse_delta'; matchedOrderId?: string; reason: string },
  ): Promise<Order> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/duplicate-resolution`, {
        method: 'POST',
        body: JSON.stringify({
          action: p.action,
          matched_order_id: p.matchedOrderId ?? null,
          reason: p.reason,
        }),
      })
      if (!res.ok) {
        let detail = 'Failed to resolve duplicate'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
      return updated
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const assignOrder = useCallback(async (orderId: string, userId: string | null): Promise<Order> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/assign`, {
        method: 'PUT',
        body: JSON.stringify({ user_id: userId }),
      })
      if (!res.ok) {
        let detail = 'Failed to assign order'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
      return updated
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const getOrderEvents = useCallback(async (orderId: string, limit?: number): Promise<OrderEventItem[]> => {
    try {
      const params = new URLSearchParams()
      if (limit != null) params.set('limit', String(limit))
      const qs = params.toString()
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/events${qs ? `?${qs}` : ''}`)
      if (!res.ok) throw new Error('Failed to fetch order events')
      const data = await res.json()
      return (data.items || []).map(transformOrderEvent)
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const getOrderMeasures = useCallback(async (orderId: string): Promise<OrderMeasures> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/measures`)
      if (!res.ok) throw new Error('Failed to fetch order measures')
      return transformMeasures(await res.json())
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const getOrderGates = useCallback(async (orderId: string): Promise<OrderGatesPayload> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/gates`)
      if (!res.ok) throw new Error('Failed to fetch order gates')
      return transformGatesPayload(await res.json())
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const updateGateState = useCallback(async (
    orderId: string, department: string, p: { state: GateState; reason?: string },
  ): Promise<OrderGatesPayload> => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/gates/${department}`, {
        method: 'PUT',
        body: JSON.stringify({ state: p.state, reason: p.reason ?? null }),
      })
      if (!res.ok) {
        let detail = 'Failed to update gate state'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      return transformGatesPayload(await res.json())
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  // Best-effort like fetchStats (used decoratively — e.g. OrdersPage's Queue
  // column initials): a failure here shouldn't flip the shared list-view
  // error banner, so it resolves to [] instead of setError()+throw.
  const getAssignees = useCallback(async (): Promise<Array<{ id: string; name: string }>> => {
    try {
      const res = await apiClient.fetch('/api/commerce/orders/assignees')
      if (!res.ok) return []
      const data = await res.json()
      return (data.items || []).map((a: Record<string, unknown>) => ({
        id: a.id as string, name: a.name as string,
      }))
    } catch {
      return []
    }
  }, [])

  const updateOrder = useCallback(async (
    orderId: string, payload: OrderUpdatePayload,
  ) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        let detail = 'Failed to update order'
        try { detail = (await res.json()).detail ?? detail } catch { /* keep default */ }
        throw new Error(detail)
      }
      const updated = transformOrder(await res.json())
      setCurrentOrder(updated)
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o))
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  const updateNotes = useCallback(async (orderId: string, notes: string) => {
    try {
      const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/notes`, {
        method: 'PUT',
        body: JSON.stringify({ notes }),
      })
      if (!res.ok) throw new Error('Failed to update notes')
      const data = await res.json()
      const updated = transformOrder(data)
      setCurrentOrder(updated)
    } catch (e) {
      setError((e as Error).message)
      throw e
    }
  }, [])

  // ── SA-401 record-first intake ─────────────────────────────────────

  async function readDetail(res: Response, fallback: string): Promise<string> {
    // F7: FastAPI's 422 `detail` is a list of error objects, not a string —
    // `new Error(detail)` on that stringifies to "[object Object]". Only a
    // string `detail` is usable; anything else falls back.
    try {
      const detail = ((await res.json()) as { detail?: unknown }).detail
      return typeof detail === 'string' ? detail : fallback
    } catch { return fallback }
  }

  const getIntakeAgendas = useCallback(async (): Promise<IntakeAgenda[]> => {
    const res = await apiClient.fetch('/api/commerce/orders/intake-agendas')
    if (!res.ok) throw new Error(await readDetail(res, 'Failed to load intake agendas'))
    return (await res.json()) as IntakeAgenda[]
  }, [])

  const extractIntake = useCallback(async (agenda: string, text: string, personaId?: string): Promise<IntakeExtraction> => {
    const res = await apiClient.fetch('/api/commerce/orders/intake/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agenda, text, ...(personaId ? { persona_id: personaId } : {}) }),
    })
    if (!res.ok) {
      const detail = await readDetail(res, 'Extraction failed')
      // 503 = not configured here (say so verbatim); 502 = the model endpoint failed.
      if (res.status === 502) throw new Error(`Extraction failed (${detail}); try again or enter the fields by hand.`)
      throw new Error(detail)
    }
    return (await res.json()) as IntakeExtraction
  }, [])

  const createQuoteRecord = useCallback(async (payload: QuoteRecordCreatePayload): Promise<Order> => {
    const res = await apiClient.fetch('/api/commerce/orders/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(await readDetail(res, 'Failed to create the quote record'))
    const order = transformOrder(await res.json())
    await Promise.all([fetchOrders({ silent: true }), fetchStats()])
    return order
  }, [fetchOrders, fetchStats])

  const getIntake = useCallback(async (orderId: string): Promise<IntakeRead> => {
    const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/intake`)
    if (!res.ok) throw new Error(await readDetail(res, 'Failed to load the intake fields'))
    return (await res.json()) as IntakeRead
  }, [])

  // SA-1456 (spec §7.5): a refusal is `{code, message}`; the message is the sentence to show.
  const linkIntakeCompany = useCallback(async (orderId: string, slot: string, companyId: string | null): Promise<IntakeRead> => {
    const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/intake/links`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slot, company_id: companyId }),
    })
    if (!res.ok) {
      let message: string | null = null
      try {
        const detail = ((await res.json()) as { detail?: unknown }).detail
        if (typeof detail === 'string') message = detail
        else if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
          message = (detail as { message: string }).message
        }
      } catch { /* the fallback below */ }
      throw new Error(message ?? LINK_FAILED)
    }
    return (await res.json()) as IntakeRead
  }, [])

  const updateIntake = useCallback(async (orderId: string, payload: IntakeUpdatePayload): Promise<IntakeRead> => {
    const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/intake`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(await readDetail(res, 'Failed to save the intake fields'))
    return (await res.json()) as IntakeRead
  }, [])

  // SA-1029: the customer updates waiting on a record, the hand-rolled lane's
  // callbacks (the Intake panel calls them; words from customerUpdateText).
  const getCustomerUpdates = useCallback(async (orderId: string): Promise<CustomerUpdate[]> => {
    const res = await apiClient.fetch(`/api/commerce/orders/${orderId}/customer-updates`)
    if (!res.ok) throw new Error(await readDetail(res, 'Failed to load the customer updates'))
    const body = (await res.json()) as { items: Record<string, unknown>[] }
    return body.items.map(transformCustomerUpdate)
  }, [])

  const customerUpdateAction = useCallback(async (orderId: string, updateId: string, action: string, payload: unknown): Promise<CustomerUpdate> => {
    try {
      return transformCustomerUpdate(await apiClient.post<Record<string, unknown>>(
        `/api/commerce/orders/${orderId}/customer-updates/${updateId}/${action}`, payload))
    } catch (e) {
      throw new Error(customerUpdateErrorMessage(e))
    }
  }, [])

  const extractCustomerUpdate = useCallback(
    (orderId: string, updateId: string) => customerUpdateAction(orderId, updateId, 'extract', {}), [customerUpdateAction])
  const applyCustomerUpdate = useCallback(
    (orderId: string, updateId: string, payload: CustomerUpdateApplyPayload) => customerUpdateAction(orderId, updateId, 'apply', payload),
    [customerUpdateAction])
  const dismissCustomerUpdate = useCallback(
    (orderId: string, updateId: string, reason?: string) => customerUpdateAction(orderId, updateId, 'dismiss', reason ? { reason } : {}),
    [customerUpdateAction])

  return {
    orders, total, capped, currentOrder, stats, isLoading, error,
    statusFilter, orderTypeFilter, searchQuery, contactId, queueStateFilter, waitingOnFilter, stepFilter,
    commercialStatusFilter, quoteStatusFilter, processStateFilter, includeRejectedFilter, sortBy, sortDir, page,
    fetchOrders, fetchOrderDetail, fetchStats, createOrder, updateOrder,
    updateStatus, enterQuote, updateNotes, recordPayment, closeOutcome, reinstateRecord,
    getFeasibility, recordFeasibility, selectScenario,
    updateQueueState, assignOrder, getOrderEvents, getOrderMeasures, getAssignees,
    getOrderGates, updateGateState,
    resolveDuplicate,
    getIntakeAgendas, extractIntake, createQuoteRecord, getIntake, updateIntake, linkIntakeCompany,
    getCustomerUpdates, extractCustomerUpdate, applyCustomerUpdate, dismissCustomerUpdate,
    setStatusFilter, setOrderTypeFilter, setSearchQuery, setContactId, setQueueStateFilter, setWaitingOnFilter,
    setStepFilter, setCommercialStatusFilter, setQuoteStatusFilter, setProcessStateFilter,
    setIncludeRejectedFilter, setSortBy, setSort, setPage, setCurrentOrder,
  }
}
